"use client";

import { ChevronDown, ChevronRight } from "lucide-react";
import { Badge } from "@/components/shadcn/badge";
import { Button } from "@/components/shadcn/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import { DataTypeBadge } from "@/icons/DataTypeIcon";
import { cn } from "@/lib/utils";
import { normalizeDataType } from "./typeCompatibility";
import type { ObjectField } from "./variableOptions";

export type { ObjectField };

const SHAPE_PREVIEW_MAX = 12;

export function isObjectType(dataType: string | undefined): boolean {
  return normalizeDataType(dataType) === "object";
}

export const UNTYPED_OBJECT_HINT =
  "This node doesn't declare the object's fields. Bind it to Get Object Properties to read a property by name.";

interface ObjectShapeFieldSpec {
  name?: string;
  label?: string | null;
  data_type?: string;
  is_list?: boolean | null;
}

/** `object_fields` off an output spec, dropping unnamed entries. */
export function toObjectFields(
  specs: ObjectShapeFieldSpec[] | null | undefined
): ObjectField[] {
  return (specs ?? [])
    .filter((f): f is ObjectShapeFieldSpec & { name: string } => !!f.name)
    .map((f) => ({
      name: f.name,
      label: f.label ?? null,
      dataType: f.data_type ?? "str",
      isList: !!f.is_list,
    }));
}

// `dense` is the tooltip form (tight rows, bare types); the default is the
// panel form, with room between rows and each type in an outlined badge.
export function ObjectShapeList({
  fields,
  max = SHAPE_PREVIEW_MAX,
  dense = false,
}: {
  fields: ObjectField[];
  max?: number;
  dense?: boolean;
}) {
  const shown = fields.slice(0, max);
  const hidden = fields.length - shown.length;
  return (
    <div className={cn("flex flex-col", dense ? "gap-1" : "gap-2")}>
      {shown.map((f) => (
        <div
          key={f.name}
          className="flex items-center justify-between gap-3 min-w-0"
        >
          <span className={cn("truncate", dense ? "text-xs" : "text-sm")}>
            {f.label ?? f.name}
          </span>
          {dense ? (
            <DataTypeBadge
              dataType={f.dataType}
              isList={f.isList}
              className="shrink-0"
            />
          ) : (
            <Badge
              variant="outline"
              className="shrink-0 rounded-md px-1.5 py-0.5 font-normal"
            >
              <DataTypeBadge dataType={f.dataType} isList={f.isList} />
            </Badge>
          )}
        </div>
      ))}
      {hidden > 0 && (
        <span className="text-xs text-muted-foreground">
          +{hidden} more field{hidden === 1 ? "" : "s"}
        </span>
      )}
    </div>
  );
}

/** Marks an object output whose shape the producing node does not declare. */
export function UntypedObjectBadge({ className }: { className?: string }) {
  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className={cn(
            "px-1 text-[10px] leading-4 font-normal text-muted-foreground",
            className
          )}
        >
          untyped
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-64">
        {UNTYPED_OBJECT_HINT}
      </TooltipContent>
    </Tooltip>
  );
}

/** Toggle that reveals a typed object's field list; reads "N fields". */
export function ObjectShapeToggle({
  count,
  expanded,
  onToggle,
  className,
}: {
  count: number;
  expanded: boolean;
  onToggle: () => void;
  className?: string;
}) {
  const Chevron = expanded ? ChevronDown : ChevronRight;
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onToggle}
      aria-expanded={expanded}
      className={cn(
        "h-5 gap-0.5 px-1 text-[10px] font-normal text-muted-foreground hover:text-foreground",
        className
      )}
    >
      <Chevron size={11} strokeWidth={2} className="shrink-0" />
      {count} field{count === 1 ? "" : "s"}
    </Button>
  );
}
